import React from 'react'
import Header from '../header'
import Footer from '../footer'
import styles from '../../styles/Text.module.css'
import Head from 'next/head'
import { GetStaticProps, InferGetStaticPropsType, } from 'next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'

const PrivacyPolicy = (_props: InferGetStaticPropsType<typeof getStaticProps>) => {
  const { t } = useTranslation("common");
  const router = useRouter();
  const baseUrl = 'https://stoneity.com';
  const currentPath = router.asPath;
  const locale = router.locale || 'en';
  const canonicalUrl = locale === 'tr'
    ? `${baseUrl}/tr${currentPath}`
    : `${baseUrl}${currentPath}`;
  const alternateLocale = locale === 'tr' ? 'en' : 'tr';
  const alternatePath = '/privacy-policy';

  return (
    <>
      <Head>
        <title>{t("seo_privacy_policy_title")}</title>
        <meta name="description" content={t("seo_privacy_policy_description")} />
        <meta name="keywords" content={locale === 'tr'
          ? "gizlilik politikası, kişisel veriler, KVKK, Stoneity, privacy policy"
          : "privacy policy, personal data, GDPR, Stoneity"} />
        <link key="canonical" rel="canonical" href={canonicalUrl} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:title" content={t("seo_privacy_policy_title")} />
        <meta property="og:description" content={t("seo_privacy_policy_description")} />
        <meta property="og:image" content={`${baseUrl}/stoneityLogo.png`} />
        <meta property="og:site_name" content="Stoneity" />
        <meta property="og:locale" content={locale === 'tr' ? 'tr_TR' : 'en_US'} />
        <meta property="og:locale:alternate" content={locale === 'tr' ? 'en_US' : 'tr_TR'} />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={canonicalUrl} />
        <meta name="twitter:title" content={t("seo_privacy_policy_title")} />
        <meta name="twitter:description" content={t("seo_privacy_policy_description")} />
        <meta name="twitter:image" content={`${baseUrl}/stoneityLogo.png`} />
        <meta name="twitter:site" content="@stoneity" />

        {/* Alternate languages */}
        <link rel="alternate" hrefLang={locale} href={canonicalUrl} />
        <link rel="alternate" hrefLang={alternateLocale} href={`${baseUrl}/${alternateLocale}${alternatePath}`} />
        <link rel="alternate" hrefLang="x-default" href={`${baseUrl}${alternatePath}`} />
      </Head>
      <Header />
      <main style={{ paddingInline: '1rem' }}>
        <div className={styles.globalTextContent}>
          <div>
            <div className={styles.globalTitle}>
              {t("confidentialityPersonelTitle")}
            </div>
            <p className={styles.globalExplainParagraph}>
              {t("confidentialityPersonelIntro")}
            </p>
          </div>
          <div>
            <div className={styles.globalSideTitle}>{t("agreementSubjectTitle")}</div>
            <div className={styles.globalExplainParagraph}>
              {t("agreementSubjectExplain")}
            </div>
          </div>
          <div>
            <div className={styles.globalSideTitle}> {t("definitionsOfThePartiesTitle")}</div>
            <div>
              <div className={styles.globalExplainParagraph}>
                {t("definitionsOfThePartiesExplain1")}
              </div>
              <div style={{ marginTop: "0" }} className={styles.globalExplainParagraph}>
                {t("definitionsOfThePartiesExplain2")}
              </div>
            </div>
          </div>
          <div>
            <div className={styles.globalSideTitle}>{t("personalDataTitle")}</div>
            <div>
              <div className={styles.globalExplainParagraph}>
                {t("personalDataSubTitle")}
              </div>
              <div className={styles.globalExplainParagraph}>
                {t("personalDataSubTitleExplain1")}
              </div>
              <div className={styles.globalExplainParagraph} style={{ marginTop: "0" }}>
                {t("personalDataSubTitleExplain2")}
              </div>
              <div className={styles.globalExplainParagraph} style={{ marginTop: "0" }}>
                {t("personalDataSubTitleExplain3")}
              </div>
              <div className={styles.globalExplainParagraph} style={{ marginTop: "0" }}>
                {t("personalDataSubTitleExplain4")}
              </div>
              <div className={styles.globalExplainParagraph}>
                {t("personalDataSubTitleExplain5")}
              </div>
            </div>
          </div>
          <div>
            <div className={styles.globalSideTitle}>{t("dataCollectionTitle")}</div>
            <div className={styles.globalExplainParagraph}>
              {t("dataCollectionExplain")}
            </div>
          </div>
          <div>
            <div className={styles.globalSideTitle}>{t("dataRetentionTitle")}</div>
            <div className={styles.globalExplainParagraph}>
              {t("dataRetentionExplain")}
            </div>
          </div>
          <div>
            <div className={styles.globalSideTitle}>{t("dataTransferTitle")}</div>
            <div>
              <div className={styles.globalExplainParagraph}>
                {t("dataTransferSubTitle")}
              </div>
              <div className={styles.globalExplainParagraph}>
                {t("dataTransferSubTitleExplain1")}
              </div>
              <div className={styles.globalExplainParagraph} style={{ marginTop: "0" }}>
                {t("dataTransferSubTitleExplain2")}
              </div>
              <div className={styles.globalExplainParagraph} style={{ marginTop: "0" }}>
                {t("dataTransferSubTitleExplain3")}
              </div>
              <div className={styles.globalExplainParagraph} style={{ marginTop: "0" }}>
                {t("dataTransferSubTitleExplain4")}
              </div>
              <div className={styles.globalExplainParagraph}>
                {t("dataTransferExplain")}
              </div>
            </div>
          </div>
          <div>
            <div className={styles.globalSideTitle}>{t("userRightsTitle")}</div>
            <div className={styles.globalExplainParagraph}>
              {t("userRightsExplain")}
            </div>
          </div>
          <div>
            <div className={styles.globalSideTitle}>{t("effectiveDateTitle")}</div>
            <div className={styles.globalExplainParagraph}>
              {t("effectiveDateExplain")}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  )
}

export const getStaticProps: GetStaticProps = async ({ locale }) => ({
  props: {
    ...(await serverSideTranslations(locale || "en", ["common"])),
  },
});

export default PrivacyPolicy;
